// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Badge, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Communication, Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CLOUDPAT_OBSERVATION_SYSTEM,
  PATIENT_TRIAGE_STATUS,
  replaceTriageStatusTags,
  type TriageSortState,
} from '@polarissleep/shared';
import type { DataTableSortStatus } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { TableAnchor } from '../../components/layout/TableAnchor';
import { type DeniedEntry, useDeniedQueue } from '../../hooks/useDeniedQueue';
import { withTenantScope } from '../../utils/tenant';
import { MoveToTestingModal } from './MoveToTestingModal';
import { ReferralOutModal } from './ReferralOutModal';
import { sortStatusToTriageSortState, triageSortStateToSortStatus } from './useTriageQueueUrlState';

interface DeniedTableProps {
  page: number;
  pageSize: number;
  search: string;
  onPageChange: (page: number) => void;
  sortState: TriageSortState | null;
  onSortChange: (next: TriageSortState | null) => void;
  hideTestPatients: boolean;
  onTotalChange?: (total: number) => void;
}

export function DeniedTable({
  page,
  pageSize,
  search,
  onPageChange,
  sortState,
  onSortChange,
  hideTestPatients,
  onTotalChange,
}: DeniedTableProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [referModalEntry, setReferModalEntry] = useState<DeniedEntry | null>(null);
  const [moveTestingEntry, setMoveTestingEntry] = useState<DeniedEntry | null>(null);
  const { entries, loading, total, refetch } = useDeniedQueue({
    page,
    pageSize,
    search,
    sort: sortState ?? undefined,
    hideTestPatients,
  });

  useEffect(() => onTotalChange?.(total), [onTotalChange, total]);

  const moveToTesting = async (entry: DeniedEntry, ahiValue: string, note: string): Promise<void> => {
    if (!entry.patientId) {
      return;
    }
    try {
      const patient = await medplum.readResource('Patient', entry.patientId);
      await medplum.updateResource({
        ...patient,
        meta: {
          ...patient.meta,
          tag: replaceTriageStatusTags(patient.meta?.tag ?? [], PATIENT_TRIAGE_STATUS.hsatResults),
        },
      });
      await medplum.createResource<Communication>(
        withTenantScope({
          resourceType: 'Communication',
          status: 'completed',
          subject: { reference: `Patient/${entry.patientId}`, display: entry.patientName },
          sent: new Date().toISOString(),
          payload: [{ contentString: note }],
        })
      );
      const ahi = Number(ahiValue);
      if (Number.isFinite(ahi)) {
        await medplum.createResource<Observation>(
          withTenantScope({
            resourceType: 'Observation',
            status: 'final',
            code: { coding: [{ system: CLOUDPAT_OBSERVATION_SYSTEM, code: 'AHI' }] },
            subject: { reference: `Patient/${entry.patientId}` },
            effectiveDateTime: new Date().toISOString(),
            valueQuantity: { value: ahi },
          })
        );
      }
      notifications.show({ color: 'green', message: 'Patient moved to Testing' });
      setMoveTestingEntry(null);
      refetch();
    } catch (error) {
      notifications.show({ color: 'red', message: error instanceof Error ? error.message : 'Unable to move patient' });
    }
  };

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const columns = [
    {
      accessor: 'patientName' as const,
      title: 'Patient',
      sortable: true,
      render: (entry: DeniedEntry) => (
        <TableAnchor
          size="sm"
          onClick={async () => {
            await navigate(`/Patient/${entry.patientId}`);
          }}
          style={{ cursor: 'pointer' }}
        >
          {entry.patientName}
        </TableAnchor>
      ),
    },
    {
      accessor: 'status' as const,
      title: 'Referred to',
      sortable: false,
      render: (entry: DeniedEntry) => (
        <div>
          <Text size="sm" fw={600}>
            In-lab polysomnography
          </Text>
          <Text size="xs" c="dimmed">
            via referral · {entry.intakeCompletedDate ? new Date(entry.intakeCompletedDate).toLocaleDateString() : '—'}
          </Text>
        </div>
      ),
    },
    {
      accessor: 'referralStatus' as const,
      title: 'Status',
      sortable: false,
      render: (entry: DeniedEntry) => (
        <Anchor size="sm" onClick={() => setReferModalEntry(entry)} style={{ cursor: 'pointer' }}>
          {entry.referralStatus === 'referred' ? 'Referral Sent' : 'Referral Pending'}
        </Anchor>
      ),
    },
    {
      accessor: 'followUp' as const,
      title: '2-week follow-up',
      sortable: false,
      render: () => <Text size="sm">—</Text>,
    },
    {
      accessor: 'evaluatedBy' as const,
      title: 'Assigned to',
      sortable: false,
      render: (entry: DeniedEntry) => (
        <Badge color="gray" variant="light" radius="xl">
          {entry.evaluatedBy ?? 'Unassigned'}
        </Badge>
      ),
    },
    {
      accessor: 'actions' as const,
      title: 'Actions',
      sortable: false,
      render: (entry: DeniedEntry) => (
        <Group gap="xs" wrap="nowrap">
          <TableAnchor size="sm" onClick={() => setReferModalEntry(entry)} style={{ cursor: 'pointer' }}>
            Update status
          </TableAnchor>
          <TableAnchor size="sm" c="#640040" onClick={() => setMoveTestingEntry(entry)} style={{ cursor: 'pointer' }}>
            Move to Testing
          </TableAnchor>
          <TableAnchor
            size="sm"
            c="dimmed"
            onClick={() => navigate(`/Patient/${entry.patientId}/Communication`)}
            style={{ cursor: 'pointer' }}
          >
            Message
          </TableAnchor>
        </Group>
      ),
    },
  ];

  const sortStatus: DataTableSortStatus<DeniedEntry> = (() => {
    const status = triageSortStateToSortStatus(sortState);
    if (!status) {
      return { columnAccessor: '', direction: 'desc' as const };
    }
    const columnAccessor = status.columnAccessor === 'name' ? 'patientName' : status.columnAccessor;
    return { columnAccessor, direction: status.direction };
  })();

  return (
    <>
      <ReferralOutModal
        opened={referModalEntry !== null}
        onClose={() => setReferModalEntry(null)}
        entry={referModalEntry}
        onSaved={() => {
          refetch();
        }}
      />
      <MoveToTestingModal
        opened={moveTestingEntry !== null}
        entry={moveTestingEntry}
        onClose={() => setMoveTestingEntry(null)}
        onMove={(ahi, note) => {
          if (moveTestingEntry) {
            moveToTesting(moveTestingEntry, ahi, note).catch(console.error);
          }
        }}
      />
      {total > 0 && (
        <Text size="sm" c="dimmed">
          {start} - {end} of {total}
        </Text>
      )}
      <DataTable
        striped
        highlightOnHover
        fetching={loading && entries.length === 0}
        records={entries}
        columns={columns}
        idAccessor="patientId"
        noRecordsText="No denied patients found"
        page={page}
        recordsPerPage={pageSize}
        totalRecords={total}
        onPageChange={onPageChange}
        paginationSize="sm"
        minHeight={200}
        sortStatus={sortStatus}
        onSortStatusChange={(status: DataTableSortStatus<DeniedEntry>) => {
          const normalizedAccessor = status.columnAccessor === 'patientName' ? 'name' : status.columnAccessor;
          const next = sortStatusToTriageSortState({ columnAccessor: normalizedAccessor, direction: status.direction });
          if (
            next &&
            sortState &&
            sortState.column === next.column &&
            sortState.direction === 'asc' &&
            next.direction === 'desc'
          ) {
            onSortChange(null);
          } else {
            onSortChange(next);
          }
        }}
      />
    </>
  );
}
